import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import AuthShell from '../components/AuthShell';
import { APPS, nameForClientId } from '../config/apps';
import OtpCodeInput from '../components/OtpCodeInput';
import PhoneInput from '../components/PhoneInput';
import { ApiError } from '../lib/api';
import { authApi } from '../lib/auth';
import { formatRemaining } from '../lib/formatDuration';
import { startGoogleLogin, type OAuthParams } from '../lib/google';
import { useCountdown } from '../hooks/useCountdown';

type Step = 'identifier' | 'signup' | 'otp';
type Method = 'phone' | 'email';

// AS OAuth params captured from the URL on mount; forwarded to /authorize on
// OTP success. All fields may be absent if the user navigates to /login directly.
function parseOAuthParams(params: URLSearchParams): OAuthParams {
  return {
    client_id: params.get('client_id') ?? '',
    redirect_uri: params.get('redirect_uri') ?? '',
    state: params.get('state') ?? '',
    code_challenge: params.get('code_challenge') ?? '',
    code_challenge_method: params.get('code_challenge_method') ?? '',
    nonce: params.get('nonce') ?? '',
    scope: params.get('scope') ?? '',
  };
}

export default function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [oauthParams] = useState<OAuthParams>(() => parseOAuthParams(searchParams));

  const [step, setStep] = useState<Step>('identifier');
  const [method, setMethod] = useState<Method>('phone');
  const [flow, setFlow] = useState<'login' | 'signup'>('login');

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const identifier = method === 'phone' ? phone : email;
  const [name, setName] = useState('');
  const [otpCode, setOtpCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [federatedProvider, setFederatedProvider] = useState<string | null>(null);
  // unix-ms timestamp when the lockout expires; drives useCountdown.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const countdown = useCountdown(lockedUntil);

  // Synchronous re-entrancy guard: `loading` state is stale within the same
  // tick, so it cannot prevent onComplete + button-click from racing.
  const inFlight = useRef(false);

  // Clear the lockout when countdown reaches 0.
  useEffect(() => {
    if (countdown === 0) setLockedUntil(null);
  }, [countdown]);

  // useCallback-stable so PhoneInput's useEffect([e164, onChange]) doesn't
  // re-fire on every parent render.
  const handlePhoneChange = useCallback((e164: string) => {
    setPhone(e164);
  }, []);

  function applyLock(retryAfterSeconds?: number) {
    const secs = retryAfterSeconds ?? 60;
    setLockedUntil(Date.now() + secs * 1000);
  }

  function isLocked() {
    return countdown !== null && countdown > 0;
  }

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current || !identifier || isLocked()) return;
    inFlight.current = true;

    setError(null);
    setFederatedProvider(null);
    setLoading(true);

    try {
      await authApi.login(identifier);
      setFlow('login');
      setStep('otp');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          applyLock(err.retryAfterSeconds);
          return;
        }
        if (err.code === 'USER_NOT_FOUND') {
          setFlow('signup');
          setStep('signup');
          return;
        }
        if (err.code === 'USE_FEDERATED_PROVIDER') {
          const provider =
            typeof err.details?.provider === 'string' ? err.details.provider : 'Google';
          setFederatedProvider(provider);
          return;
        }
        setError(t('auth.errors.unknownError'));
      } else {
        setError(t('auth.errors.networkError'));
      }
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }

  async function handleSignupSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current || !identifier || !name.trim() || isLocked()) return;
    inFlight.current = true;

    setError(null);
    setFederatedProvider(null);
    setLoading(true);

    try {
      await authApi.register(identifier);
      setFlow('signup');
      setStep('otp');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          applyLock(err.retryAfterSeconds);
          return;
        }
        if (err.code === 'ACCOUNT_EXISTS') {
          setError(t('auth.errors.accountExists'));
          return;
        }
        if (err.code === 'USE_FEDERATED_PROVIDER') {
          const provider =
            typeof err.details?.provider === 'string' ? err.details.provider : 'Google';
          setFederatedProvider(provider);
          return;
        }
        setError(t('auth.errors.unknownError'));
      } else {
        setError(t('auth.errors.networkError'));
      }
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }

  async function handleResend() {
    if (inFlight.current || isLocked()) return;
    inFlight.current = true;
    setError(null);
    setOtpCode('');
    setLoading(true);
    try {
      await (flow === 'signup' ? authApi.register(identifier) : authApi.login(identifier));
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        applyLock(err.retryAfterSeconds);
      } else {
        setError(t('auth.errors.unknownError'));
      }
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }

  async function handleVerifyOtp(code: string) {
    if (inFlight.current || isLocked()) return;
    inFlight.current = true;
    setError(null);
    setLoading(true);

    try {
      await (flow === 'signup'
        ? authApi.registerVerify(identifier, code, name, oauthParams.client_id || undefined)
        : authApi.verifyOtp(identifier, code, oauthParams.client_id || undefined));

      // Handoff: backend set __Host-session; navigate to /authorize so it mints
      // the code and 302s to redirect_uri. Same-host relative URL — no open redirect.
      const nonEmptyOauthParams = Object.fromEntries(
        Object.entries(oauthParams).filter(([, v]) => v !== ''),
      );
      const qs = new URLSearchParams({ response_type: 'code', ...nonEmptyOauthParams });
      window.location.assign(`/authorize?${qs.toString()}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          applyLock(err.retryAfterSeconds);
          return;
        }
        if (err.code === 'WRONG_CODE') {
          setError(t('auth.errors.incorrectCode'));
          setOtpCode('');
          return;
        }
        setError(t('auth.errors.expiredCode'));
      } else {
        setError(t('auth.errors.networkError'));
      }
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }

  function handleGoogleLogin() {
    startGoogleLogin(oauthParams);
  }

  const locked = isLocked();
  const lockMessage = locked
    ? t('auth.errors.tooManyAttempts', { time: formatRemaining(countdown!) })
    : null;

  // Without an authorization request (client_id + redirect_uri arrive via /authorize) there is no app to sign into.
  const hasOAuthRequest = Boolean(oauthParams.client_id && oauthParams.redirect_uri);
  // Display name for the requesting app, or null for an unrecognised/absent client_id
  // — never render attacker-controllable text.
  const appName = nameForClientId(oauthParams.client_id);
  if (!hasOAuthRequest) {
    return (
      <AuthShell>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('auth.login.title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2.5 }}>
          {t('auth.login.noRequest')}
        </Typography>
        <Stack spacing={1}>
          {APPS.map((app) => (
            <Button
              key={app.url}
              component="a"
              href={app.url}
              variant="outlined"
              fullWidth
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ justifyContent: 'space-between', py: 1.1 }}
            >
              {app.name}
            </Button>
          ))}
        </Stack>
      </AuthShell>
    );
  }

  if (step === 'identifier') {
    return (
      <AuthShell>
        <Box component="form" onSubmit={handleSendCode} noValidate>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {t('auth.login.title')}
          </Typography>
          {appName && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('auth.login.continueTo', { app: appName })}
            </Typography>
          )}

          <Stack spacing={2.5} sx={{ mt: 3 }}>
            {lockMessage && <Alert severity="warning">{lockMessage}</Alert>}

            {federatedProvider && (
              <Alert severity="info">
                {t('auth.errors.useFederatedProvider', {
                  provider:
                    federatedProvider.charAt(0).toUpperCase() + federatedProvider.slice(1),
                })}
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={method === 'phone' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setMethod('phone');
                  setError(null);
                  setFederatedProvider(null);
                }}
                sx={{ flex: 1 }}
              >
                {t('auth.login.switchToPhone')}
              </Button>
              <Button
                variant={method === 'email' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setMethod('email');
                  setError(null);
                  setFederatedProvider(null);
                }}
                sx={{ flex: 1 }}
              >
                {t('auth.login.switchToEmail')}
              </Button>
            </Box>

            {method === 'phone' ? (
              <PhoneInput
                value={phone}
                onChange={handlePhoneChange}
                label={t('auth.login.phoneLabel')}
                disabled={loading || locked}
                autoFocus
              />
            ) : (
              <TextField
                label={t('auth.login.emailLabel')}
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                fullWidth
                required
                autoFocus
                autoComplete="email"
                disabled={loading || locked}
                inputMode="email"
              />
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading || locked || !identifier}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                  {t('auth.login.submitting')}
                </>
              ) : (
                t('auth.login.submitButton')
              )}
            </Button>

            <Divider>{t('auth.federatedCTA.orContinueWith')}</Divider>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              {t('auth.federatedCTA.google')}
            </Button>
          </Stack>
        </Box>
      </AuthShell>
    );
  }

  if (step === 'signup') {
    return (
      <AuthShell>
        <Box component="form" onSubmit={handleSignupSubmit} noValidate>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {t('auth.signup.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.signup.noAccountPrompt', { identifier })}
          </Typography>

          <Stack spacing={2.5} sx={{ mt: 3 }}>
            {lockMessage && <Alert severity="warning">{lockMessage}</Alert>}

            {federatedProvider && (
              <Alert severity="info">
                {t('auth.errors.useFederatedProvider', {
                  provider:
                    federatedProvider.charAt(0).toUpperCase() + federatedProvider.slice(1),
                })}
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label={t('auth.signup.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              autoFocus
              autoComplete="name"
              disabled={loading || locked}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading || locked || !name.trim()}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                  {t('auth.login.submitting')}
                </>
              ) : (
                t('auth.signup.submitButton')
              )}
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={() => {
                setStep('identifier');
                setError(null);
                setFederatedProvider(null);
              }}
            >
              {t('auth.login.title')}
            </Button>
          </Stack>
        </Box>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('auth.otp.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {t('auth.otp.instruction', { destination: identifier })}
        </Typography>

        <Stack spacing={2.5}>
          {lockMessage && <Alert severity="warning">{lockMessage}</Alert>}

          {error && <Alert severity="error">{error}</Alert>}

          <OtpCodeInput
            value={otpCode}
            onChange={setOtpCode}
            onComplete={handleVerifyOtp}
            disabled={loading || locked}
            autoFocus
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || locked || otpCode.length < 6}
            onClick={() => {
              if (otpCode.length === 6) handleVerifyOtp(otpCode);
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                {t('auth.otp.submitting')}
              </>
            ) : (
              t('auth.otp.submitButton')
            )}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="text"
              size="small"
              disabled={loading || locked}
              onClick={handleResend}
            >
              {locked
                ? t('auth.otp.resendIn', { time: formatRemaining(countdown!) })
                : t('auth.otp.resendButton')}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setStep('identifier');
                setError(null);
                setName('');
                setOtpCode('');
              }}
            >
              {t('auth.otp.changeDestination', {
                type: method === 'phone' ? t('auth.login.phoneLabel') : t('auth.login.emailLabel'),
              })}
            </Button>
          </Box>
        </Stack>
      </Box>
    </AuthShell>
  );
}
